import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import MentorUpgradeModal from './MentorUpgradeModal';
import CardManagementModal from './CardManagementModal';
import { Settings, CreditCard, Shield, Bell, User, LogOut, ChevronRight, Check, X } from 'lucide-react';
import { authFetch } from '../utils/authFetch';

export default function MentorAccountSettingsModal({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availablePlans, setAvailablePlans] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [showCardManagement, setShowCardManagement] = useState(false);
  const [userData, setUserData] = useState(null);
  const [mentorSession, setMentorSession] = useState(null);

  // Get mentor session from localStorage
  useEffect(() => {
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      if (session && session.ownerId && session.email) {
        setMentorSession(session);
      }
    } catch (_) {
      setMentorSession(null);
    }
  }, [isOpen]);

  // Fetch user data from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isOpen) return;
      
      // If we have a Firebase user, fetch their document
      if (user?.uid) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else if (mentorSession) {
        // For mentor_session, try to find the mentor invite document
        try {
          // Try direct document lookup by email_mentor format
          const inviteDocId = `${mentorSession.email.toLowerCase()}_mentor`;
          const inviteRef = doc(db, 'mentorInvites', inviteDocId);
          const inviteDoc = await getDoc(inviteRef);
          
          if (inviteDoc.exists()) {
            const inviteData = inviteDoc.data();
            // Also fetch the organization owner's document to get more info
            try {
              const ownerRef = doc(db, 'users', mentorSession.ownerId);
              const ownerDoc = await getDoc(ownerRef);
              if (ownerDoc.exists()) {
                const ownerData = ownerDoc.data();
                // Combine invite data with owner data, marking as organization mentor
                setUserData({
                  ...inviteData,
                  mentorType: 'organization',
                  ownerId: mentorSession.ownerId,
                  email: mentorSession.email,
                  // Get member since from invite creation date
                  createdAt: inviteData.createdAt || inviteData.acceptedAt
                });
              } else {
                setUserData({
                  ...inviteData,
                  mentorType: 'organization',
                  ownerId: mentorSession.ownerId,
                  email: mentorSession.email,
                  createdAt: inviteData.createdAt || inviteData.acceptedAt
                });
              }
            } catch (ownerError) {
              console.error('Error fetching owner data:', ownerError);
              setUserData({
                ...inviteData,
                mentorType: 'organization',
                ownerId: mentorSession.ownerId,
                email: mentorSession.email,
                createdAt: inviteData.createdAt || inviteData.acceptedAt
              });
            }
          } else {
            // Fallback: create a basic userData object from session
            setUserData({
              mentorType: 'organization',
              ownerId: mentorSession.ownerId,
              email: mentorSession.email,
              createdAt: mentorSession.at ? new Date(mentorSession.at) : null
            });
          }
        } catch (error) {
          console.error('Error fetching mentor invite:', error);
          // Fallback: create a basic userData object from session
          setUserData({
            mentorType: 'organization',
            ownerId: mentorSession.ownerId,
            email: mentorSession.email,
            createdAt: mentorSession.at ? new Date(mentorSession.at) : null
          });
        }
      }
    };

    fetchUserData();
  }, [isOpen, user, mentorSession]);

  useEffect(() => {
    if (isOpen) {
      const userId = user?.uid || mentorSession?.ownerId;
      if (userId) {
        fetchMembershipStatus(userId);
        fetchAvailablePlans();
      }
    }
  }, [isOpen, user, mentorSession]);

  const fetchMembershipStatus = async (userId) => {
    try {
      setLoading(true);
      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Membership data received:', data.membership);
        console.log('Billing instructions:', data.membership?.billingInstructions);
        setMembershipStatus(data.membership);
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
      setError('Failed to load membership information');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/mentor-plans`);
      if (response.ok) {
        const data = await response.json();
        setAvailablePlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching available plans:', error);
    }
  };

  const handleUpgradePlan = (newPlan) => {
    setSelectedUpgradePlan(newPlan);
    setShowUpgradeModal(true);
  };

  const handleUpgradeConfirm = (result) => {
    setSuccess(result.billing.confirmation);
    fetchMembershipStatus(); // Refresh membership status
    setShowUpgradeModal(false);
    setSelectedUpgradePlan(null);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const userId = user?.uid || mentorSession?.ownerId;
      
      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/mentor-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: userId
        })
      });

      const result = await response.json();
      
      if (result.ok) {
        setSuccess(result.billing.confirmation);
        await fetchMembershipStatus(userId); // Refresh membership status
      } else {
        setError(result.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    // Navigate to membership page for upgrade
    window.location.href = '/membership';
  };


  const handleLogout = async () => {
    try {
      // Clear mentor_session if it exists
      try {
        localStorage.removeItem('mentor_session');
        window.dispatchEvent(new Event('mentor_session_changed'));
      } catch (_) {}
      
      // Logout Firebase user if exists
      if (user) {
        await logout();
      }
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Get account type display text
  const getAccountType = () => {
    if (userData?.mentorType === 'organization') {
      return 'Art Mentor (Organization)';
    } else if (userData?.mentorType === 'single' || userData?.mentorType === 'mentor') {
      return 'Art Mentor (Single)';
    } else if (mentorSession) {
      // If we have mentor_session but no userData, assume organization (since org mentors use access codes)
      return 'Art Mentor (Organization)';
    }
    return 'Art Mentor (Single)';
  };

  // Get user email
  const getUserEmail = () => {
    if (user?.email) return user.email;
    if (mentorSession?.email) return mentorSession.email;
    return 'N/A';
  };

  // Get user ID
  const getUserId = () => {
    if (user?.uid) return user.uid;
    if (mentorSession?.ownerId) return mentorSession.ownerId;
    return 'N/A';
  };

  // Get member since date
  const getMemberSince = () => {
    if (user?.metadata?.creationTime) return user.metadata.creationTime;
    if (userData?.createdAt) return userData.createdAt;
    if (mentorSession?.at) return new Date(mentorSession.at);
    return null;
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    
    let date;
    // Handle Firestore timestamp objects
    if (dateInput && typeof dateInput === 'object' && dateInput._seconds) {
      date = new Date(dateInput._seconds * 1000);
    } else if (dateInput && typeof dateInput.toDate === 'function') {
      date = dateInput.toDate();
    } else {
      date = new Date(dateInput);
    }
    
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'trialing': return 'text-blue-600 bg-blue-100';
      case 'expired': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'trialing': return 'Trial';
      case 'expired': return 'Expired';
      default: return 'Unknown';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Account Settings</h2>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/90 hover:text-white text-3xl leading-none p-1"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex h-[600px]">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('account')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'account' 
                    ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <User className="w-5 h-5" />
                Account Info
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'subscription' 
                    ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <CreditCard className="w-5 h-5" />
                Subscription
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'security' 
                    ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Shield className="w-5 h-5" />
                Security
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'notifications' 
                    ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Bell className="w-5 h-5" />
                Notifications
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeTab === 'account' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Account Information</h3>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <p className="text-gray-900">{getUserEmail()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                      <p className="text-gray-900">{getAccountType()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
                      <p className="text-gray-900">{formatDate(getMemberSince())}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                      <p className="text-gray-900 font-mono text-sm break-all">{getUserId()}</p>
                    </div>
                    {mentorSession && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Owner ID</label>
                        <p className="text-gray-900 font-mono text-sm break-all">{mentorSession.ownerId}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Subscription Management</h3>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Current Plan */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">Current Plan</h4>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(membershipStatus?.status)}`}>
                          {getStatusText(membershipStatus?.status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Tier</label>
                          <p className="text-gray-900 font-semibold">
                            {membershipStatus?.tier ? membershipStatus.tier.replace('mentor-', '').toUpperCase() : 'Basic'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                          <p className="text-gray-900">Monthly</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Next Billing</label>
                          <p className="text-gray-900">
                            {formatDate(membershipStatus?.currentPeriodEnd)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Plan Features */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Plan Features</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">Unlimited Artwork Creation</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">AI Art Analysis</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">Mentor Dashboard Access</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">Student Progress Tracking</span>
                        </div>
                      </div>
                    </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() => setShowPlanSelection(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                            View Upgrade Options
                          </button>
                          <button
                            onClick={() => setShowCardManagement(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <CreditCard className="w-4 h-4" />
                            Manage Card
                          </button>
                          {membershipStatus?.status === 'active' && (
                            <button
                              onClick={handleCancelSubscription}
                              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Cancel Subscription
                            </button>
                          )}
                        </div>

                    {/* Billing Information */}
                    {membershipStatus?.billingInstructions && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Billing Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                            <p className="text-gray-900">
                              {membershipStatus.billingInstructions.currency === 'INR' ? '₹' : '$'}
                              {membershipStatus.billingInstructions.amount}/{membershipStatus.billingInstructions.interval}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                            <p className="text-gray-900">{membershipStatus.billingInstructions.currency}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Next Billing Date</label>
                            <p className="text-gray-900">{formatDate(membershipStatus.billingInstructions.nextBillingDate)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                            <p className="text-gray-900">{membershipStatus.billingInstructions.provider || 'Stripe'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <X className="w-5 h-5 text-red-500" />
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      <p className="text-green-700">{success}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Security Settings</h3>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Password</h4>
                        <p className="text-sm text-gray-600">Last updated: {formatDate(user?.metadata?.lastSignInTime)}</p>
                      </div>
                      <button className="px-4 py-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors">
                        Change
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
                        <p className="text-sm text-gray-600">Add an extra layer of security</p>
                      </div>
                      <button className="px-4 py-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors">
                        Enable
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Notification Preferences</h3>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Email Notifications</h4>
                        <p className="text-sm text-gray-600">Receive updates about your account and students</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Student Progress Updates</h4>
                        <p className="text-sm text-gray-600">Get notified when students complete assignments</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Plan Selection Modal */}
        {showPlanSelection && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowPlanSelection(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">Choose Your Upgrade Plan</h3>
                  <button 
                    onClick={() => setShowPlanSelection(false)} 
                    className="text-white/90 hover:text-white text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">Select a plan to see upgrade details and pricing.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {availablePlans && Object.entries(availablePlans).map(([planKey, planData]) => {
                    const isCurrentPlan = membershipStatus?.tier === planKey;
                    return (
                      <div 
                        key={planKey} 
                        className={`border rounded-lg p-4 transition-colors ${
                          isCurrentPlan 
                            ? 'border-gray-300 bg-gray-50' 
                            : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!isCurrentPlan) {
                            setSelectedUpgradePlan(planKey);
                            setShowPlanSelection(false);
                            setShowUpgradeModal(true);
                          }
                        }}
                      >
                        <div className="text-center">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            {planKey.replace('mentor-', '').toUpperCase()}
                            {isCurrentPlan && <span className="text-sm text-gray-500 ml-2">(Current)</span>}
                          </h4>
                          <div className="text-2xl font-bold text-blue-600 mb-2">
                            {planData.currency === 'INR' ? '₹' : '$'}{planData.amount}
                            <span className="text-sm text-gray-500">/{planData.interval}</span>
                          </div>
                          <button
                            disabled={isCurrentPlan}
                            className={`w-full px-4 py-2 rounded-lg transition-colors ${
                              isCurrentPlan
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        <MentorUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setSelectedUpgradePlan(null);
          }}
          currentPlan={membershipStatus?.tier}
          newPlan={selectedUpgradePlan}
          onConfirm={handleUpgradeConfirm}
        />

        {/* Card Management Modal */}
        <CardManagementModal
          isOpen={showCardManagement}
          onClose={() => setShowCardManagement(false)}
          userType="mentor"
        />
      </div>
    </div>
  );
}